import React, { useEffect, useState, useCallback, useContext, useRef } from 'react';
import { CustomInnerHeaderProps } from 'ag-grid-react';
import { useDataTableContext } from '../shared/Context';
import { DataTableContext } from '../../DataTable/DataTableState';
import TooltipPortal from './TooltipPortal';
import { DataTableOption } from '../../DataTable/DataTypes';

interface HeaderCheckboxProps extends CustomInnerHeaderProps {
  onToggleSelectAll?: (selectAll: boolean, pageIndex: number) => void;
  primaryKey?: string;
  dataTableOption?: DataTableOption;
}

const HeaderCheckbox: React.FC<HeaderCheckboxProps> = (props) => {
  const [selectionMap, setSelectionMap] = useState<Map<number, boolean>>(new Map());
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState(false);
  const [pendingCheckboxValue, setPendingCheckboxValue] = useState(false);
  const { setSelectedRowCount, isSelectAllActive, totalEntriesRef, selectedRowData, deSelectedRowData } = useDataTableContext(); 
  const DataTableContextObj = useContext(DataTableContext);
  const { primaryKey = '', dataTableOption } = props;
  const checkboxRef = useRef<HTMLDivElement>(null);
  const SELECT_THIS_PAGE = 'Select rows on this page';
  const SELECT_ALL = 'Select all rows';
  const disableSelectAllOption = dataTableOption?.disableSelectAllOption ?? false;

  const getRowKey = useCallback((data: any) => {
    return primaryKey ? data?.[primaryKey] : JSON.stringify(data);
  }, [primaryKey]);

  const addRowToMap = useCallback((map: Map<string, any>, data: any) => {
    const key = getRowKey(data);
    if (key != null) {
      map.set(key, data);
    }
  }, [getRowKey]);

  const removeRowFromMap = useCallback((map: Map<string, any>, data: any) => {
    const key = getRowKey(data);
    if (key != null) {
      map.delete(key);
    }
  }, [getRowKey]);

  const calculatePageSelectionStatus = useCallback(() => {
    const pageIndex = props.api.paginationGetCurrentPage();
    const pageSize = props.api.paginationGetPageSize();
    const startIndex = pageIndex * pageSize;
    const endIndex = Math.min(startIndex + pageSize, props.api.getDisplayedRowCount());
    let allSelected = true;
    let hasSelectableRows = false;

    for (let i = startIndex; i < endIndex; i++) {
      const rowNode = props.api.getDisplayedRowAtIndex(i);
      if (rowNode?.selectable) {
        hasSelectableRows = true;
        if (!rowNode.isSelected()) {
          allSelected = false;
          break;
        }
      }
    }
    return { allSelected: hasSelectableRows && allSelected, pageIndex };
  }, [props.api]);


  const updateHeaderCheckboxState = useCallback(() => {
    const { allSelected, pageIndex } = calculatePageSelectionStatus();
    if (isAllSelected === allSelected && selectionMap.get(pageIndex) === allSelected) {
      return;
    }
    const newMap = new Map(selectionMap);
    newMap.set(pageIndex, allSelected);
    setSelectionMap(newMap);
    setIsAllSelected(allSelected);
  
    // console.log("updateHeaderCheckboxState ", selectedRowData.current.size, deSelectedRowData.current.size, selectionMap);
  
    // To ensure that when all rows are deSelected, the deSelectedRowData is cleared and isSelectAllActive is set to false. Which makes the selectAll popup visible
    if(deSelectedRowData.current.size === totalEntriesRef.current){
        deSelectedRowData.current.clear();
        isSelectAllActive.current = false;
    }
  }, [calculatePageSelectionStatus, isAllSelected, selectionMap]);


  const updateSelectionCounts = useCallback(() => {
    const newSelectedMap = new Map<string, any>(selectedRowData.current ?? new Map());
    const newDeSelectedMap = new Map(deSelectedRowData.current ?? new Map());

    if (isSelectAllActive.current) {
      const selectedRowCountValue = totalEntriesRef.current - newDeSelectedMap.size;
      setSelectedRowCount(selectedRowCountValue);
    } else {
      setSelectedRowCount(newSelectedMap.size);
    }

    const selectedRows = Array.from(newSelectedMap.values());
    DataTableContextObj.setSearchState((prevSearchState: any) => ({
      ...prevSearchState,
      selectedRowIds: selectedRows,
    }));
  }, [setSelectedRowCount, totalEntriesRef, DataTableContextObj]);



  const handlePageChange = useCallback(() => {
    updateHeaderCheckboxState();
  }, [props.api, updateHeaderCheckboxState]);

  const handleCurrentPageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    // console.log("handleCurrentPageChange when selectall is active", checked)
    const pageIndex = props.api.paginationGetCurrentPage();
    const pageSize = props.api.paginationGetPageSize();
    const startIndex = pageIndex * pageSize;
    const endIndex = Math.min(startIndex + pageSize, props.api.getDisplayedRowCount());
    const newSelectedMap = new Map<string, any>(selectedRowData.current);
    const newDeSelectedMap = new Map<string, any>(deSelectedRowData.current);

    for (let i = startIndex; i < endIndex; i++) {
      const rowNode = props.api.getDisplayedRowAtIndex(i);
      if (rowNode?.selectable) {
        rowNode.setSelected(checked);
        const data = rowNode?.data;
        if (data) {
          if (checked) {
            addRowToMap(newSelectedMap, data);
            removeRowFromMap(newDeSelectedMap, data);
          } else {
            removeRowFromMap(newSelectedMap, data);
            if (isSelectAllActive.current) {
              addRowToMap(newDeSelectedMap, data);
            }
          }
        }
      }
    }

    selectedRowData.current = newSelectedMap;
    deSelectedRowData.current = newDeSelectedMap;
    const newMap = new Map(selectionMap);
    newMap.set(pageIndex, checked);
    setSelectionMap(newMap);
    setIsAllSelected(checked);
    updateSelectionCounts();
    props.onToggleSelectAll?.(checked, pageIndex);
  }, [props.api, props.onToggleSelectAll, selectionMap, addRowToMap, removeRowFromMap, updateSelectionCounts]);

  const handleSelectAll = useCallback((checked: boolean) => {
    const pageIndex = props.api.paginationGetCurrentPage();
    const newSelectedMap = new Map();
    const newDeSelectedMap = new Map();
    isSelectAllActive.current = checked;
    props.api.forEachNode((rowNode) => {
      if (rowNode?.selectable) {
        rowNode.setSelected(checked);
        const data = rowNode?.data;
        if (data) {
          if (checked) {
            addRowToMap(newSelectedMap, data);
          } else {
            addRowToMap(newDeSelectedMap, data);
          }
        }
      }
    });

    selectedRowData.current = newSelectedMap;
    deSelectedRowData.current = checked ? new Map() : newDeSelectedMap;
    
    const newSelectionMap = new Map();
    const totalPages = Math.ceil(totalEntriesRef.current / props.api.paginationGetPageSize());
    for (let i = 0; i < totalPages; i++) {
      newSelectionMap.set(i, checked);
    }
    
    setSelectionMap(newSelectionMap);
    setIsAllSelected(checked);
    
    updateSelectionCounts();
    props.onToggleSelectAll?.(checked, pageIndex);
  }, [props.api, props.onToggleSelectAll, addRowToMap, updateSelectionCounts, totalEntriesRef]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    // to make true - checked
    const pageSize = props.api.paginationGetPageSize();
    // console.log("pageSize", pageSize)
    // console.log("handleChange when selectall is not active", checked )
    if(dataTableOption?.dataType === 'memory') {
      handleSelectAll(checked);
    }
    else{
    if (checked && !isAllSelected && totalEntriesRef.current > pageSize && !disableSelectAllOption) {
      setPendingCheckboxValue(checked);
      setShowPopup(true);
    } else {
      // if (isSelectAllActive.current) {
      //   handleSelectAll(checked);
      // } else {
        handleCurrentPageChange(e);
      // }
    }
    }
  }, [isAllSelected, handleSelectAll, handleCurrentPageChange]);

  const handlePopupSelection = useCallback((scope: 'page' | 'all') => {
    if (scope === 'page') {
      const syntheticEvent = { target: { checked: pendingCheckboxValue } } as React.ChangeEvent<HTMLInputElement>;
      handleCurrentPageChange(syntheticEvent);
    } else {
      handleSelectAll(pendingCheckboxValue);
    }
    setShowPopup(false);
  }, [pendingCheckboxValue, handleCurrentPageChange, handleSelectAll]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const popup = document.getElementById('dropdown-root');
      if (popup && !popup.contains(e.target as Node)) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [showPopup]);

  useEffect(() => {
    const handleSelectionChange = () => {
      updateHeaderCheckboxState();
      updateSelectionCounts();
    };
    props.api.addEventListener('selectionChanged', handleSelectionChange);
    return () => {
      props.api.removeEventListener('selectionChanged', handleSelectionChange);
    };
  }, [props.api, updateHeaderCheckboxState, updateSelectionCounts]);

  useEffect(() => {
    const handlePaginationChange = () => {
      // Used setTimeout to ensure AG-Grid has finished rendering the new page
      setTimeout(() => {
        handlePageChange();
      }, 0);
    };
    props.api.addEventListener('paginationChanged', handlePaginationChange);
    // Initialize on mount
    setTimeout(() => {
      handlePageChange();
    }, 0);
    return () => {
      props.api.removeEventListener('paginationChanged', handlePaginationChange);
    };
  }, [props.api, handlePageChange]);

  const totalRecordsCount = DataTableContextObj.searchState?.totalRecords ?? 0;

  if (totalRecordsCount === 0) {
    return null;
  }

  return (
    <>
      <div
        ref={checkboxRef}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
        }}
      >
        <input
          type="checkbox"
          // onChange={isSelectAllActive.current ? handleCurrentPageChange : handleChange}
          onChange={handleChange}
          checked={isAllSelected}
          className="grey-checkbox"
          style={{
            cursor: 'pointer',
            width: '16px',
            height: '16px',
          }}
        />
      </div>

      <TooltipPortal isOpen={showPopup} targetRef={checkboxRef}>
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '6px', 
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '2px'
            }}
            onClick={() => handlePopupSelection('page')}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <input
              type="checkbox"
              checked={false}
              readOnly
              style={{ marginRight: '8px', pointerEvents: 'none' }}
            />
            {SELECT_THIS_PAGE}
          </div>
          
          <hr style={{ margin: '6px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
          
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '2px'
            }}
            onClick={() => handlePopupSelection('all')}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <input
              type="checkbox"
              checked={false}
              readOnly
              style={{ marginRight: '8px', pointerEvents: 'none' }}
            />
            {SELECT_ALL}
          </div>
      </TooltipPortal>
    </>
  );
};

export default HeaderCheckbox;